import { Box, Button, Typography, IconButton, Tooltip } from "@mui/material";
import { Edit, Save, Add, RestartAlt } from "@mui/icons-material";
import { useState, useCallback, useMemo } from "react";
import {
  Responsive,
  useContainerWidth,
  verticalCompactor,
} from "react-grid-layout";
import type { Layout, LayoutItem, ResponsiveLayouts } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { useParams } from "react-router-dom";
import { useDashboardData } from "../../hooks/useDashboardData";
import {
  useDashboardConfig,
  useSaveDashboardConfig,
} from "../../hooks/useDashboardConfig";
import DashboardWidgetRenderer from "./DashboardWidgetRenderer";
import AddWidgetDialog from "./AddWidgetDialog";
import {
  DashboardWidget,
  WIDGET_TYPES,
} from "../../types/dashboard/DashboardConfig";
import { useTranslation } from "react-i18next";
import { useFeatureFlagToken } from "../../hooks/useFeatureFlag";
import { FeatureFlagName } from "../../types/feature-flags/featureFlag";

export const formatTime = (ms: number) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
};

const GRID_COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const ROW_HEIGHT = 80;

const Dashboard = () => {
  const { t } = useTranslation("dashboards");
  const canEdit = useFeatureFlagToken(FeatureFlagName.EditableDashboards);
  const { customerId } = useParams();
  const { data: dashboardData, isLoading: isDataLoading } =
    useDashboardData(customerId);
  const { data: configData, isLoading: isConfigLoading } =
    useDashboardConfig(customerId);
  const saveMutation = useSaveDashboardConfig(customerId);

  const { width, containerRef, mounted } = useContainerWidth();

  const [isEditing, setIsEditing] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [localWidgets, setLocalWidgets] = useState<DashboardWidget[] | null>(
    null
  );

  // Use local state while editing, otherwise use server data
  const widgets = useMemo(() => {
    if (localWidgets) return localWidgets;
    return configData?.widgets ?? [];
  }, [localWidgets, configData]);

  // Convert widgets to react-grid-layout format
  const layouts: ResponsiveLayouts = useMemo(() => {
    const lg: LayoutItem[] = widgets.map((w) => ({
      i: w.id,
      x: w.layout.x,
      y: w.layout.y,
      w: w.layout.w,
      h: w.layout.h,
      minW: w.layout.minW ?? 2,
      minH: w.layout.minH ?? 2,
    }));
    return { lg };
  }, [widgets]);

  const handleLayoutChange = useCallback(
    (layout: Layout) => {
      if (!isEditing) return;
      setLocalWidgets((prev) => {
        const current = prev ?? configData?.widgets ?? [];
        const layoutMap = new Map(layout.map((l) => [l.i, l]));
        return current.map((widget) => {
          const layoutItem = layoutMap.get(widget.id);
          if (!layoutItem) return widget;
          return {
            ...widget,
            layout: {
              ...widget.layout,
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            },
          };
        });
      });
    },
    [isEditing, configData]
  );

  const handleEdit = () => {
    setLocalWidgets(configData?.widgets ?? []);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (localWidgets) {
      saveMutation.mutate(localWidgets);
    }
    setIsEditing(false);
    setLocalWidgets(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setLocalWidgets(null);
  };

  const handleRemoveWidget = (widgetId: string) => {
    setLocalWidgets((prev) => {
      const current = prev ?? configData?.widgets ?? [];
      return current.filter((w) => w.id !== widgetId);
    });
  };

  const handleAddWidget = (widget: DashboardWidget) => {
    setLocalWidgets((prev) => {
      const current = prev ?? configData?.widgets ?? [];
      return [...current, widget];
    });
  };

  const handleResetDefaults = () => {
    const defaults: DashboardWidget[] = [
      {
        id: "legal-hold",
        type: WIDGET_TYPES.LEGAL_HOLD,
        title: "Interactions in Legal Hold",
        layout: { x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
      },
      {
        id: "purging",
        type: WIDGET_TYPES.PURGING,
        title: "Purging (30 Days)",
        layout: { x: 3, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
      },
      {
        id: "total-interactions",
        type: WIDGET_TYPES.TOTAL_INTERACTIONS,
        title: "Total Interactions",
        layout: { x: 6, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
      },
      {
        id: "tags",
        type: WIDGET_TYPES.TAGS,
        title: "Tags",
        layout: { x: 9, y: 0, w: 3, h: 2, minW: 2, minH: 2 },
      },
      {
        id: "interactions-by-year",
        type: WIDGET_TYPES.INTERACTIONS_BY_YEAR,
        title: "Interactions by Year",
        layout: { x: 0, y: 2, w: 8, h: 4, minW: 4, minH: 3 },
      },
      {
        id: "media-type",
        type: WIDGET_TYPES.MEDIA_TYPE,
        title: "Interactions by Media Type",
        layout: { x: 8, y: 2, w: 4, h: 4, minW: 3, minH: 3 },
      },
    ];
    setLocalWidgets(defaults);
  };

  const handleAskArchie = () => {
    globalThis.dispatchEvent(new CustomEvent("open-archie"));
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" color="primary" fontWeight={600}>
          {t("dashboard")}
        </Typography>
        {canEdit && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {isEditing ? (
              <>
                <Tooltip title={t("resetToDefaults")}>
                  <IconButton onClick={handleResetDefaults} size="small">
                    <RestartAlt />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={() => setAddDialogOpen(true)}
                >
                  {t("addWidget")}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancel}
                >
                  {t("cancel")}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? t("saving") : t("save")}
                </Button>
              </>
            ) : (
              <Tooltip title={t("customizeDashboard")}>
                <IconButton onClick={handleEdit} size="small">
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Grid Layout */}
      <div ref={containerRef as React.RefObject<HTMLDivElement>}>
        {mounted && !isConfigLoading && (
          <Responsive
            className="layout"
            width={width}
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={GRID_COLS}
            rowHeight={ROW_HEIGHT}
            dragConfig={{ enabled: isEditing, bounded: false, handle: ".drag-handle", threshold: 3 }}
            resizeConfig={{ enabled: isEditing, handles: ["se"] }}
            onLayoutChange={handleLayoutChange}
            compactor={verticalCompactor}
            margin={[16, 16]}
          >
            {widgets.map((widget) => (
              <div key={widget.id} style={{ overflow: "hidden" }}>
                <DashboardWidgetRenderer
                  widget={widget}
                  dashboardData={dashboardData}
                  isLoading={isDataLoading}
                  isEditing={isEditing}
                  onRemove={handleRemoveWidget}
                />
              </div>
            ))}
          </Responsive>
        )}
      </div>

      {/* Add Widget Dialog */}
      <AddWidgetDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAddWidget}
        onAskArchie={handleAskArchie}
      />
    </Box>
  );
};

export default Dashboard;
